import React from 'react';
import { Calendar, MapPin, CheckCircle, ExternalLink, GraduationCap } from 'lucide-react';

interface WorkshopData {
  college: string;
  date: string;
  title: string;
  description: string;
  highlights: string[];
  link?: string;
  logo: string;
  logoColor: string;
}

export const WorkshopHighlights = () => {
  const workshops: WorkshopData[] = [
    {
      college: "REVA University",
      date: "June 5, 2025",
      title: "Great Products Emerge When Curiosity Meets Clarity",
      description: "Alumni Impact Talk focusing on product thinking and innovation mindset for engineering students.",
      highlights: ["Product Thinking Workshop", "Alumni Success Stories", "Interactive Q&A Session"],
      link: "https://www.linkedin.com/posts/a2zassist_leadershipineducation-revauniversity-alumnimatters-ugcPost-7336442687966846976-iTwO/",
      logo: "R",
      logoColor: "#6B4423"
    },
    {
      college: "M.S. Engineering College",
      date: "June 6, 2025",
      title: "From Problem Solving to Product Thinking",
      description: "First-Year Career Readiness Session designed to bridge academic learning with industry requirements.",
      highlights: ["Career Readiness", "Industry Insights", "Hands-on Workshops"],
      link: "https://www.linkedin.com/posts/a2zassist_campustocareer-a2zcampus-a2zassist-activity-7336769213711175680-2Bnk/",
      logo: "M",
      logoColor: "#6B4423"
    },
    {
      college: "Vaagdevi Institute",
      date: "July 26, 2023",
      title: "Campus Drive & Technical Excellence",
      description: "Comprehensive placement preparation and technical skill development session for final year students.",
      highlights: ["Placement Preparation", "Technical Interviews", "Industry Connections"],
      logo: "V",
      logoColor: "#6B4423"
    }
  ];

  return (
    <section className="py-4 sm:py-6 md:py-8" style={{backgroundColor: '#FAF7F2'}}>
      <div className="container mx-auto px-3 sm:px-4 md:px-6">
        <div className="text-center mb-4 sm:mb-6 md:mb-8">
          <div className="inline-flex items-center px-3 sm:px-4 md:px-4 py-2 md:py-2 bg-white rounded-full shadow-lg border font-bold mb-2 sm:mb-2 md:mb-3" style={{borderColor: '#C4A052', color: '#6B4423'}}>
            <img 
              src="/A2ZAssist%20logo.png" 
              alt="A2Z Assist Logo" 
              className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 mr-2 md:mr-3 object-contain"
            />
            Recent Success Stories
          </div>
          <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-2 sm:mb-3 md:mb-4 px-2" style={{color: '#2C2420'}}>
            Workshop Highlights
          </h2>
          <p className="text-sm sm:text-base md:text-base max-w-4xl mx-auto leading-relaxed px-3" style={{color: '#2C2420'}}>
            Transforming student potential through industry-aligned workshops that bridge the gap 
            between academic learning and professional success.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
          {workshops.map((workshop, index) => (
            <div key={index} className="group bg-white rounded-3xl p-3 sm:p-4 md:p-5 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-gray-200">
              <div className="flex items-start mb-2 sm:mb-3 md:mb-4">
                <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-2xl flex items-center justify-center mr-2 sm:mr-2 md:mr-3 shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0" style={{backgroundColor: workshop.logoColor}}>
                  <span className="text-white font-bold text-sm sm:text-base md:text-lg">{workshop.logo}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center text-xs md:text-xs mb-1 md:mb-1" style={{color: '#2C2420'}}>
                    <Calendar className="w-3 h-3 md:w-3 md:h-3 mr-1 md:mr-2 flex-shrink-0" style={{color: '#C4A052'}} />
                    <span className="truncate">{workshop.date}</span>
                  </div>
                  <div className="flex items-center text-xs md:text-xs font-medium" style={{color: '#6B4423'}}>
                    <MapPin className="w-3 h-3 md:w-3 md:h-3 mr-1 md:mr-2 flex-shrink-0" style={{color: '#C4A052'}} />
                    <span className="truncate">{workshop.college}</span>
                  </div>
                </div>
              </div>
              
              <h3 className="text-sm sm:text-base md:text-lg font-bold mb-2 md:mb-2 group-hover:opacity-80 transition-colors leading-tight" style={{color: '#2C2420'}}>
                {workshop.title}
              </h3>
              
              <p className="mb-2 sm:mb-2 md:mb-3 leading-relaxed text-xs md:text-sm" style={{color: '#2C2420'}}>
                {workshop.description}
              </p>
              
              <div className="space-y-1 md:space-y-1 mb-2 sm:mb-2 md:mb-3">
                {workshop.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-center text-xs md:text-xs">
                    <CheckCircle className="w-3 h-3 md:w-3 md:h-3 mr-2 md:mr-2 flex-shrink-0" style={{color: '#8B9D7A'}} />
                    <span style={{color: '#6B4423'}}>{highlight}</span>
                  </div>
                ))}
              </div>
              
              {workshop.link && (
                <a
                  href={workshop.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center font-medium transition-colors group-hover:translate-x-1 transform duration-300 hover:opacity-80 text-xs sm:text-xs md:text-sm"
                  style={{color: '#36454F'}}
                >
                  <span className="truncate">View Workshop Details</span>
                  <ExternalLink className="w-3 h-3 md:w-3 md:h-3 ml-1 md:ml-2 flex-shrink-0" />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};